import { Alert, Button, Input, Loader, Modal } from '@lifebit-ai/lds'
import { Formik, useFormikContext } from 'formik'
import React from 'react'
import * as Yup from 'yup'

import {
  DATA_ACCESS_REQUEST_APPROVAL_MODAL_APPROVE_LABEL,
  DATA_ACCESS_REQUEST_APPROVAL_MODAL_TITLE,
  DATA_ACCESS_REQUEST_AUTOMATIONS_SECTION_DESCRIPTION,
  DATA_ACCESS_REQUEST_AUTOMATIONS_SECTION_TITLE,
  DATA_ACCESS_REQUEST_STUDY_ID_IMMUTABLE_WARNING,
  DATA_ACCESS_REQUEST_STUDY_ID_INPUT_LABEL,
  DATA_ACCESS_REQUEST_STUDY_ID_INPUT_PLACEHOLDER
} from 'modules/requests/data-access-requests/DataAccessRequestDetails/constants'
import { useDebouncedDataAccessRequestStudyIdValidation } from 'modules/requests/data-access-requests/DataAccessRequestDetails/hooks/useDebouncedDataAccessRequestStudyIdValidation'
import { buildStudyIdSchema } from 'services/shared/schemas/studyIdSchema'
import { normalizeStudyIdInput } from 'services/shared/studyIdUtils'

import * as styles from './DataAccessRequestApprovalModal.styles'

export const DATA_ACCESS_REQUEST_APPROVAL_MODAL_TEST_ID = 'data-access-request-approval-modal'
export const DATA_ACCESS_REQUEST_APPROVAL_STUDY_ID_FIELD_TEST_ID = 'data-access-request-approval-study-id-field'
export const DATA_ACCESS_REQUEST_APPROVAL_SUBMIT_TEST_ID = 'data-access-request-approval-submit'
export const DATA_ACCESS_REQUEST_APPROVAL_EVALUATION_ALERT_TEST_ID = 'data-access-request-approval-evaluation-alert'

const APPROVAL_ERROR_MESSAGE =
  'There was an error while approving the data access request. Please try again or contact the administrator.'

interface ApprovalModalFormValues {
  studyId: string
}

const buildValidationSchema = (autoSharingOnApprovalEnabled: boolean) =>
  Yup.object({
    studyId: autoSharingOnApprovalEnabled ? buildStudyIdSchema().notRequired() : Yup.string().optional()
  })

interface DataAccessRequestApprovalModalContentProps {
  autoSharingOnApprovalEnabled: boolean
  description: string
  isError: boolean
  isLoading: boolean
  isOpen: boolean
  onClose: () => void
  requestId: string
}

const DataAccessRequestApprovalModalContent = ({
  autoSharingOnApprovalEnabled,
  description,
  isError,
  isLoading,
  isOpen,
  onClose,
  requestId
}: DataAccessRequestApprovalModalContentProps) => {
  const { errors, getFieldProps, handleSubmit, isValid, setFieldValue, touched, values } =
    useFormikContext<ApprovalModalFormValues>()

  const { canApprove, evaluationAlerts, isValidating, isValidationPending, validationErrorMessage } =
    useDebouncedDataAccessRequestStudyIdValidation({
      enabled: isOpen && autoSharingOnApprovalEnabled,
      requestId,
      studyId: values.studyId
    })

  const normalizedStudyId = normalizeStudyIdInput(values.studyId)
  const isSubmitDisabled =
    !isValid || isLoading || (autoSharingOnApprovalEnabled && normalizedStudyId !== '' && !canApprove)

  const fieldStatusType =
    touched.studyId && errors.studyId !== undefined
      ? 'error'
      : validationErrorMessage !== undefined
      ? 'error'
      : undefined

  const fieldStatusText =
    touched.studyId && errors.studyId !== undefined
      ? errors.studyId
      : validationErrorMessage !== undefined
      ? validationErrorMessage
      : undefined

  return (
    <Modal
      data-testid={DATA_ACCESS_REQUEST_APPROVAL_MODAL_TEST_ID}
      size="medium"
      destroyOnClose
      footer={
        <styles.Footer>
          <Button
            data-testid={DATA_ACCESS_REQUEST_APPROVAL_SUBMIT_TEST_ID}
            disabled={isSubmitDisabled}
            htmlType="submit"
            loading={isLoading}
            size="large"
            type="primary"
            onClick={() => {
              handleSubmit()
            }}
          >
            {DATA_ACCESS_REQUEST_APPROVAL_MODAL_APPROVE_LABEL}
          </Button>
        </styles.Footer>
      }
      open={isOpen}
      title={DATA_ACCESS_REQUEST_APPROVAL_MODAL_TITLE}
      onCancel={onClose}
    >
      <styles.Wrapper>
        {isError && (
          <Alert showIcon type="danger">
            {APPROVAL_ERROR_MESSAGE}
          </Alert>
        )}
        <styles.Description>{description}</styles.Description>
        {autoSharingOnApprovalEnabled ? (
          <styles.AutomationsSection>
            <styles.AutomationsSectionHeader>
              <styles.AutomationsSectionTitle>
                {DATA_ACCESS_REQUEST_AUTOMATIONS_SECTION_TITLE}
              </styles.AutomationsSectionTitle>
              <styles.AutomationsSectionDescription>
                {DATA_ACCESS_REQUEST_AUTOMATIONS_SECTION_DESCRIPTION}
              </styles.AutomationsSectionDescription>
            </styles.AutomationsSectionHeader>
            <Input
              data-testid={DATA_ACCESS_REQUEST_APPROVAL_STUDY_ID_FIELD_TEST_ID}
              iconAfter={isValidating ? <Loader size="small" text={false} /> : null}
              label={DATA_ACCESS_REQUEST_STUDY_ID_INPUT_LABEL}
              placeholder={DATA_ACCESS_REQUEST_STUDY_ID_INPUT_PLACEHOLDER}
              status={fieldStatusType !== undefined ? { type: fieldStatusType, text: fieldStatusText } : undefined}
              {...getFieldProps('studyId')}
              onChange={(event: React.FormEvent<HTMLInputElement>) => {
                void setFieldValue('studyId', (event.target as HTMLInputElement).value.trim())
              }}
            />
            <styles.ImmutableWarning>{DATA_ACCESS_REQUEST_STUDY_ID_IMMUTABLE_WARNING}</styles.ImmutableWarning>
            {!isValidationPending &&
              evaluationAlerts.map((evaluationAlert, index) => (
                <styles.EvaluationAlert
                  key={`${evaluationAlert.type}-${index}`}
                  data-testid={DATA_ACCESS_REQUEST_APPROVAL_EVALUATION_ALERT_TEST_ID}
                  showIcon
                  type={evaluationAlert.type}
                >
                  {evaluationAlert.message}
                </styles.EvaluationAlert>
              ))}
          </styles.AutomationsSection>
        ) : null}
      </styles.Wrapper>
    </Modal>
  )
}

interface DataAccessRequestApprovalModalProps {
  autoSharingOnApprovalEnabled: boolean
  description: string
  isError: boolean
  isLoading: boolean
  isOpen: boolean
  onApprove: (studyId?: string) => void
  onClose: () => void
  requestId: string
}

export const DataAccessRequestApprovalModal = ({
  autoSharingOnApprovalEnabled,
  description,
  isError,
  isLoading,
  isOpen,
  onApprove,
  onClose,
  requestId
}: DataAccessRequestApprovalModalProps): JSX.Element => (
  <Formik<ApprovalModalFormValues>
    initialValues={{ studyId: '' }}
    validationSchema={buildValidationSchema(autoSharingOnApprovalEnabled)}
    validateOnMount
    onSubmit={values => {
      // Formik keeps isSubmitting true until onSubmit's returned Promise settles; resolve
      // immediately because approval is fire-and-forget via the parent mutation (isLoading).
      if (!autoSharingOnApprovalEnabled) {
        onApprove()
        return Promise.resolve()
      }

      const studyId = normalizeStudyIdInput(values.studyId)
      if (studyId !== '') {
        onApprove(studyId)
      } else {
        onApprove()
      }

      return Promise.resolve()
    }}
  >
    <DataAccessRequestApprovalModalContent
      autoSharingOnApprovalEnabled={autoSharingOnApprovalEnabled}
      description={description}
      isError={isError}
      isLoading={isLoading}
      isOpen={isOpen}
      requestId={requestId}
      onClose={onClose}
    />
  </Formik>
)
